<script setup lang="ts">
import { RestaurantAreaType } from '~/lib/interfaces/restaurant'
import { GTMContentGroup, GtmEventName, GTMFormNameType } from '~/lib/models/gtm'
import { RateOrderInput, RatingErrorStatus } from '~/lib/models/rating'
import { RateOrderStepType, RateOrderStep } from '~/lib/models/ui/modal-steps'
import { ToastType } from '~/lib/models/ui/toast'
import { RateOrderBody, StoreOrder } from '~/lib/services/store/order/order.dto'

const appStore = useAppStore()
const authStore = useAuthStore()
const { trackPageView, trackOnCloseModal, trackSurveySubmit, trackClosePopup } = useGTM()
const { t } = useLocale()
const { $toast } = useNuxtApp()
const { orderServiceRef } = useStoreService()
const orderService = orderServiceRef()

const orderId = appStore.ratingIntentData!.orderId || ''
const order = ref<StoreOrder | undefined>()
const actualStep = ref<RateOrderStepType>()
const stars = ref<number>()
const isOK = ref<boolean>(true)

const type = computed(() => appStore.ratingIntentData?.type as RestaurantAreaType)
const body = ref<RateOrderInput>({
  orderRating: 0,
  token: appStore.ratingIntentData?.token || '',
  type: type.value,
  orderDetails: [],
  buttonSatisfaction: undefined,
})

const isStarsStep = computed(() => actualStep.value === RateOrderStep.STARS)
const isRatingStep = computed(() => actualStep.value === RateOrderStep.RATING)
const isRatingFinal = computed(() => actualStep.value === RateOrderStep.FINAL_STEP)
const configuration = computed(() => appStore.ecommerceConfig?.ratingOrdersV2)

const restaurantCode = computed(
  () => appStore.ratingIntentData?.restCode ?? order.value?.restaurant.code
)

const surveySubmitInput = computed(() => ({
  orderId,
  deliveryType: type.value,
  restaurantCode: restaurantCode.value,
}))

const setStars = (rating: number) => {
  const hasPreviousRating = !!body.value.orderRating
  stars.value = rating
  body.value.orderRating = rating

  // only track survey_submit if user is submitting new rating at first time
  if (!hasPreviousRating)
    trackSurveySubmit({ ...surveySubmitInput.value, comment: `${body.value.orderRating}/5` })

  setStep(RateOrderStep.RATING)
  trackPageView(
    {
      cg: GTMContentGroup.primary.USER,
      cg2: GTMContentGroup.secondary.RATE_ORDER,
      cg3: GTMContentGroup.tertiary.FORM,
    },
    true
  )
}

const setStep = (step: RateOrderStepType) => {
  actualStep.value = step
}

const onClose = ({ closeModal }: { closeModal: boolean }) => {
  if (closeModal) {
    // track close_popup
    trackClosePopup({
      event: GtmEventName.CLOSE_POPUP,
      form_name: GTMFormNameType.RATE_ORDER,
      ecommerce: {
        transaction_id: orderId,
      },
    })
  }
  appStore.setRatingIntentData(undefined)
  trackOnCloseModal()
}

const onSubmit = async ({ isOk, input }: { isOk: boolean; input: string }) => {
  trackSurveySubmit({
    ...surveySubmitInput.value,
    comment: `${body.value.orderRating}/5${input ? `-${input}` : ''}`, // 3/5-comment
  })

  isOK.value = isOk
  if (isOK.value) body.value.orderCommentsGeneral = input
  else body.value.orderCommentsKoDetail = input
  try {
    // Request
    await orderService.rateOrder({
      countryCode: appStore.countryCode,
      orderId,
      rating: body.value,
    })
    appStore.setRatingBackupForOrder(orderId, undefined)
    setStep(RateOrderStep.FINAL_STEP)
    trackPageView(
      {
        cg: GTMContentGroup.primary.USER,
        cg2: GTMContentGroup.secondary.RATE_ORDER,
        cg3: GTMContentGroup.tertiary.THANKS,
      },
      true
    )
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.log('🚀 ~ file: RateOrderView.vue:55 ~ onSubmit ~ error:', error)
    const status = error?.response?.status || 500
    checkStatus(status)
    onClose({ closeModal: false })
  }
}

const setDetails = (details: RateOrderBody['orderDetails']) => {
  body.value.orderDetails = details
}

const setButtonSatisfaction = (value: boolean) => {
  body.value.buttonSatisfaction = value
}

const checkStatus = (status?: number): boolean => {
  if (!status || !configuration.value) return true
  let content = t('rating.toasts.UNKNOWN')
  switch (status) {
    case 611:
      content = t(`rating.toasts.${RatingErrorStatus.NOT_FOUND}`)
      break
    case 622:
      content = t(`rating.toasts.${RatingErrorStatus.ALREADY_RATED}`)
      break
    case 623:
      content = t(`rating.toasts.${RatingErrorStatus.RATING_EXPIRED}`)
      break
  }
  $toast.show({
    content,
    type: ToastType.ERROR,
  })
  return false
}

watch(
  body,
  () => {
    appStore.setRatingBackupForOrder(orderId, body.value)
  },
  { deep: true }
)

onMounted(() => {
  const mustContinue = checkStatus(appStore.ratingIntentData?.status)
  if (!mustContinue) {
    appStore.setRatingIntentData(undefined)
    return
  }
  if (!restaurantCode.value) {
    // get async order if no restaurant code param (rating from email)
    orderService
      .getOrderById({
        customerToken: authStore.token ?? '',
        countryCode: appStore.countryCode,
        orderId,
      })
      .then(orderData => {
        order.value = orderData
      })
      .catch(() => {})
  }
  const elemPrevious = appStore.ratingBackupRecord?.[orderId]
  if (elemPrevious) {
    body.value = elemPrevious
  }
  setStep(RateOrderStep.STARS)
  trackPageView(
    {
      cg: GTMContentGroup.primary.USER,
      cg2: GTMContentGroup.secondary.RATE_ORDER,
    },
    true
  )
})
</script>
<template>
  <main v-if="!!appStore.ratingIntentData" data-test="order-rating">
    <RatingStarsStep
      v-if="isStarsStep"
      :initial-stars="body.orderRating"
      @close="onClose"
      @set-stars="setStars"
    />
    <LazyRatingFormStep
      v-else-if="isRatingStep && configuration"
      :data="body"
      :type="type"
      :config="configuration"
      @close="onClose"
      @change-details="setDetails"
      @button-satisfaction="setButtonSatisfaction"
      @submit="onSubmit"
    />
    <LazyRatingFinalStep v-if="isRatingFinal" :is-ok="isOK" :type="type" @close="onClose" />
  </main>
</template>
